import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, Title, Text, SimpleGrid, TextInput, Skeleton, Card, Select, Group } from '@mantine/core';
import { IconSearch, IconBooks } from '@tabler/icons-react';
import {
  usePublicCoursesService,
  usePublicCategoriesService,
  usePublicMinistryService,
} from '@actschurch/ccms-sdk';
import { CourseCard } from '@/components/CourseCard';

export default function CoursesCatalogPage() {
  const publicCoursesService = usePublicCoursesService();
  const publicCategoriesService = usePublicCategoriesService();
  const publicMinistryService = usePublicMinistryService();

  const [q, setQ] = useState('');
  const [ministryId, setMinistryId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);

  // Enriched view: every row already carries categoryName + ministryName so we
  // don't need to join client-side. Falls back to filter dropdowns hitting the
  // bare lookup endpoints (categoryID list etc.) which haven't changed.
  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['public', 'courses', 'view', ministryId],
    queryFn: () => publicCoursesService.listView(ministryId ? Number(ministryId) : undefined),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['public', 'categories'],
    queryFn: () => publicCategoriesService.list(),
  });
  const { data: ministries = [] } = useQuery({
    queryKey: ['public', 'ministries'],
    queryFn: () => publicMinistryService.list(),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = courses;
    if (categoryId) {
      const catNum = Number(categoryId);
      rows = rows.filter((c) => c.categoryID === catNum);
    }
    if (needle) {
      rows = rows.filter((c) => (c.name ?? '').toLowerCase().includes(needle));
    }
    return rows;
  }, [courses, categoryId, q]);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={1}>Course catalogue</Title>
        <Text c="dimmed">
          Every course available to you right now. Filter by ministry or category to narrow down.
        </Text>
      </Stack>

      <Group gap="sm" wrap="wrap">
        <TextInput
          leftSection={<IconSearch size={16} />}
          placeholder="Search courses"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          size="md"
          radius="xl"
          style={{ flex: 1, minWidth: 240 }}
        />
        <Select
          placeholder="All ministries"
          clearable
          searchable
          data={ministries.map((m) => ({ value: String(m.ministryID), label: m.name }))}
          value={ministryId}
          onChange={setMinistryId}
          radius="xl"
          size="md"
          w={220}
        />
        <Select
          placeholder="All categories"
          clearable
          searchable
          data={categories.map((c) => ({ value: String(c.categoryID), label: c.name ?? '—' }))}
          value={categoryId}
          onChange={setCategoryId}
          radius="xl"
          size="md"
          w={220}
        />
      </Group>

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} height={280} radius="lg" />
          ))}
        </SimpleGrid>
      ) : filtered.length === 0 ? (
        <Card withBorder padding="xl" radius="lg">
          <Stack align="center" gap="sm">
            <IconBooks size={36} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">No courses match your search.</Text>
          </Stack>
        </Card>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {filtered.map((course) => (
            <CourseCard
              key={course.courseID}
              course={course}
              categoryName={course.categoryName}
              categoryColor={course.categoryColor}
              ministryName={course.ministryName}
            />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
